import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../context/AuthContext';
import {
  Title, Card, Table, Button, Group, Text, Modal,
  NumberInput, Select, Stack, Loader, Center, ActionIcon,
  Tooltip, Divider, Badge, Box, Paper
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconPencil, IconTrash, IconCopy, IconWallet, IconAdjustments } from '@tabler/icons-react';
import 'dayjs/locale/it';

const AREE = [
  { value: 'usave_fco', label: 'U-Save FCO' },
  { value: 'usave_cia', label: 'U-Save CIA' },
  { value: 'commercio_veicoli', label: 'Commercio Veicoli' },
  { value: 'consulenza_nlt', label: 'Consulenza NLT' },
  { value: 'generale', label: 'Generale' },
];

const EMPTY_FORM = {
  mese: null,
  dipendente_id: null,
  costo_a: 0,
  costo_b: 0,
  costo_c: 0,
  note: '',
  allocazioni_override: [],
};

export default function CostiPersonale() {
  const [records, setRecords] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    Promise.all([
      api.get('/costi-personale-dipendente'),
      api.get('/dipendenti'),
    ]).then(([r1, r2]) => {
      setRecords(r1.data);
      setDipendenti(r2.data.filter(d => d.attivo));
    }).catch(() => {
      notifications.show({ title: 'Errore', message: 'Impossibile caricare i dati', color: 'red' });
    }).finally(() => setLoading(false));
  }, []);

  const reload = async () => {
    const res = await api.get('/costi-personale-dipendente');
    setRecords(res.data);
  };

  const grouped = useMemo(() => {
    const map = {};
    records.forEach(r => {
      const key = r.mese.substring(0, 7);
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [records]);

  const dipendentiSelect = dipendenti.map(d => ({
    value: String(d.id),
    label: `${d.nome} ${d.cognome}`.trim(),
  }));

  const resetForm = () => { setEditing(null); setForm({ ...EMPTY_FORM }); };

  const openEdit = (r) => {
    setEditing(r);
    const allocs = r.allocazioni_override && r.allocazioni_override.length > 0
      ? r.allocazioni_override.map(a => ({ area: a.area, percentuale: a.percentuale }))
      : getDefaultAllocazioni(String(r.dipendente_id));
    setForm({
      mese: new Date(r.mese + 'T00:00:00'),
      dipendente_id: String(r.dipendente_id),
      costo_a: r.costo_a || 0,
      costo_b: r.costo_b || 0,
      costo_c: r.costo_c || 0,
      note: r.note || '',
      allocazioni_override: allocs,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.mese) { notifications.show({ title: 'Errore', message: 'Seleziona un mese', color: 'red' }); return; }
    if (!form.dipendente_id) { notifications.show({ title: 'Errore', message: 'Seleziona un dipendente', color: 'red' }); return; }
    const d = form.mese;
    const meseStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    const payload = {
      mese: meseStr,
      dipendente_id: parseInt(form.dipendente_id),
      costo_a: form.costo_a || 0,
      costo_b: form.costo_b || 0,
      costo_c: form.costo_c || 0,
      note: form.note || null,
      allocazioni_override: form.allocazioni_override.length > 0 ? form.allocazioni_override : null,
    };
    try {
      if (editing) {
        await api.put(`/costi-personale-dipendente/${editing.id}`, payload);
        notifications.show({ title: 'Successo', message: 'Record aggiornato', color: 'green' });
      } else {
        await api.post('/costi-personale-dipendente', payload);
        notifications.show({ title: 'Successo', message: 'Record inserito', color: 'green' });
      }
      setShowModal(false);
      resetForm();
      await reload();
    } catch (err) {
      notifications.show({ title: 'Errore', message: err.response?.data?.detail || 'Errore nel salvataggio', color: 'red' });
    }
  };

  const handleDuplica = async (meseKey) => {
    const meseOrigine = `${meseKey}-01`;
    try {
      const res = await api.post(`/costi-personale-dipendente/duplica?mese_origine=${meseOrigine}`);
      notifications.show({ title: 'Successo', message: res.data.message, color: 'green' });
      await reload();
    } catch (err) {
      notifications.show({ title: 'Errore', message: err.response?.data?.detail || 'Errore duplicazione', color: 'red' });
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Eliminare il record di ${r.dipendente_nome}?`)) return;
    try {
      await api.delete(`/costi-personale-dipendente/${r.id}`);
      notifications.show({ title: 'Successo', message: 'Eliminato', color: 'green' });
      await reload();
    } catch { notifications.show({ title: 'Errore', message: 'Errore eliminazione', color: 'red' }); }
  };

  const fmt = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const fmtMese = (key) => {
    const [y, m] = key.split('-');
    return new Date(+y, +m - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  };

  const getDefaultAllocazioni = (dipId) => {
    const dip = dipendenti.find(d => String(d.id) === dipId);
    if (dip && dip.allocazioni && dip.allocazioni.length > 0) {
      return dip.allocazioni.map(a => ({ area: a.area, percentuale: a.percentuale }));
    }
    return [{ area: 'usave_fco', percentuale: 100 }];
  };

  const addOverrideAlloc = () => {
    setForm({ ...form, allocazioni_override: [...form.allocazioni_override, { area: 'usave_fco', percentuale: 0 }] });
  };

  const removeOverrideAlloc = (idx) => {
    setForm({ ...form, allocazioni_override: form.allocazioni_override.filter((_, i) => i !== idx) });
  };

  const updateOverrideAlloc = (idx, field, value) => {
    const allocs = [...form.allocazioni_override];
    allocs[idx] = { ...allocs[idx], [field]: value };
    setForm({ ...form, allocazioni_override: allocs });
  };

  const totaleForm = (form.costo_a || 0) + (form.costo_b || 0) + (form.costo_c || 0);

  if (loading) return <Center h={400}><Loader size="lg" color="teal" /></Center>;

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group>
          <IconWallet size={28} color="rgb(32, 83, 87)" />
          <Title order={2}>Costi Personale</Title>
        </Group>
        <Button leftSection={<IconPlus size={18} />} onClick={() => { resetForm(); setShowModal(true); }}>
          Nuovo Record
        </Button>
      </Group>

      {grouped.length === 0 && (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text ta="center" c="dimmed" py="xl">Nessun costo personale inserito</Text>
        </Card>
      )}

      {grouped.map(([meseKey, rows]) => {
        const totA = rows.reduce((s, r) => s + (r.costo_a || 0), 0);
        const totB = rows.reduce((s, r) => s + (r.costo_b || 0), 0);
        const totC = rows.reduce((s, r) => s + (r.costo_c || 0), 0);
        const totMese = totA + totB + totC;
        return (
          <Card key={meseKey} shadow="sm" padding={isMobile ? 'sm' : 'lg'} radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Text fw={700} size={isMobile ? 'md' : 'lg'} tt="capitalize">{fmtMese(meseKey)}</Text>
              <Group gap="xs">
                <Badge color="teal" variant="subtle" size={isMobile ? 'sm' : 'lg'}>{fmt(totMese)}</Badge>
                <Tooltip label="Duplica tutti i record al mese successivo">
                  <ActionIcon variant="subtle" color="teal" onClick={() => handleDuplica(meseKey)}>
                    <IconCopy size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
            <Table striped highlightOnHover style={isMobile ? { fontSize: '0.8rem' } : undefined}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Risorsa</Table.Th>
                  {!isMobile && <Table.Th ta="right">A</Table.Th>}
                  {!isMobile && <Table.Th ta="right">B</Table.Th>}
                  {!isMobile && <Table.Th ta="right">C</Table.Th>}
                  <Table.Th ta="right">Totale</Table.Th>
                  <Table.Th>Azioni</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map(r => (
                  <Table.Tr key={r.id} onClick={() => isMobile && openEdit(r)} style={isMobile ? { cursor: 'pointer' } : undefined}>
                    <Table.Td><Text size={isMobile ? 'xs' : 'sm'} fw={500}>{r.dipendente_nome}</Text></Table.Td>
                    {!isMobile && <Table.Td ta="right">{fmt(r.costo_a)}</Table.Td>}
                    {!isMobile && <Table.Td ta="right">{fmt(r.costo_b)}</Table.Td>}
                    {!isMobile && <Table.Td ta="right">{fmt(r.costo_c)}</Table.Td>}
                    <Table.Td ta="right"><Text size={isMobile ? 'xs' : 'sm'} fw={700}>{fmt(r.totale)}</Text></Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <ActionIcon variant="subtle" color="blue" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); openEdit(r); }}>
                          <IconPencil size={isMobile ? 14 : 16} />
                        </ActionIcon>
                        <ActionIcon variant="subtle" color="red" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); handleDelete(r); }}>
                          <IconTrash size={isMobile ? 14 : 16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
                <Table.Tr style={{ backgroundColor: 'var(--mantine-color-teal-0)' }}>
                  <Table.Td><Text size={isMobile ? 'xs' : 'sm'} fw={700}>{isMobile ? 'TOTALE' : `TOTALE ${fmtMese(meseKey).toUpperCase()}`}</Text></Table.Td>
                  {!isMobile && <Table.Td ta="right"><Text fw={700}>{fmt(totA)}</Text></Table.Td>}
                  {!isMobile && <Table.Td ta="right"><Text fw={700}>{fmt(totB)}</Text></Table.Td>}
                  {!isMobile && <Table.Td ta="right"><Text fw={700}>{fmt(totC)}</Text></Table.Td>}
                  <Table.Td ta="right"><Text size={isMobile ? 'xs' : 'sm'} fw={700} c="teal">{fmt(totMese)}</Text></Table.Td>
                  <Table.Td />
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Card>
        );
      })}

      <Modal
        opened={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editing ? 'Modifica Costo Personale' : 'Nuovo Costo Personale'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <MonthPickerInput
              label="Mese"
              locale="it"
              valueFormat="MMMM YYYY"
              value={form.mese}
              onChange={v => setForm({ ...form, mese: v })}
              required
            />
            <Select
              label="Risorsa"
              data={dipendentiSelect}
              value={form.dipendente_id}
              onChange={v => setForm({ ...form, dipendente_id: v, allocazioni_override: getDefaultAllocazioni(v) })}
              searchable
              required
              placeholder="Seleziona risorsa"
            />
            <Divider label="Costi mensili" />
            <NumberInput
              label="A"
              decimalScale={2} prefix="€ "
              thousandSeparator="." decimalSeparator=","
              value={form.costo_a}
              onChange={v => setForm({ ...form, costo_a: v || 0 })}
            />
            <NumberInput
              label="B"
              decimalScale={2} prefix="€ "
              thousandSeparator="." decimalSeparator=","
              value={form.costo_b}
              onChange={v => setForm({ ...form, costo_b: v || 0 })}
            />
            <NumberInput
              label="C"
              decimalScale={2} prefix="€ "
              thousandSeparator="." decimalSeparator=","
              value={form.costo_c}
              onChange={v => setForm({ ...form, costo_c: v || 0 })}
            />
            {form.dipendente_id && (
              <>
                <Divider label="Allocazione per area" labelProps={{ fw: 600 }} />
                <Paper p="md" withBorder>
                  <Group justify="space-between" mb="sm">
                    <Text size="sm" c="dimmed">
                      <IconAdjustments size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      Ripartizione costo per area (modificabile per questo mese)
                    </Text>
                    <Button size="xs" variant="subtle" leftSection={<IconPlus size={14} />} onClick={addOverrideAlloc}>
                      Aggiungi
                    </Button>
                  </Group>
                  {form.allocazioni_override.map((alloc, idx) => (
                    <Group key={idx} mb="xs">
                      <Select data={AREE} value={alloc.area} onChange={v => updateOverrideAlloc(idx, 'area', v)} style={{ flex: 1 }} />
                      <NumberInput value={alloc.percentuale} onChange={v => updateOverrideAlloc(idx, 'percentuale', v)}
                                   suffix="%" min={0} max={100} style={{ width: 100 }} />
                      <ActionIcon variant="subtle" color="red" onClick={() => removeOverrideAlloc(idx)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  ))}
                  {form.allocazioni_override.length > 0 && (
                    <Text size="sm" c={Math.abs(form.allocazioni_override.reduce((s, a) => s + (a.percentuale || 0), 0) - 100) < 0.01 ? 'green' : 'red'} fw={600}>
                      Totale: {form.allocazioni_override.reduce((s, a) => s + (a.percentuale || 0), 0)}%
                    </Text>
                  )}
                </Paper>
              </>
            )}
            <Group justify="space-between" align="center">
              <Text fw={700} c="teal" size="lg">
                Totale: {fmt(totaleForm)}
              </Text>
            </Group>
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => { setShowModal(false); resetForm(); }}>Annulla</Button>
              <Button type="submit">Salva</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
